/**
 * logger.ts — Logger maison réutilisable
 * --------------------------------------
 * À déposer dans chaque projet sous `lib/logger.ts`.
 *
 * - Actif en développement, silencieux en production (sauf erreurs).
 * - Catégorise et colore les logs dans la console.
 * - Garde un buffer en mémoire que le DebugOverlay peut lire et afficher.
 *
 * Usage :
 *   import { log } from "@/lib/logger";
 *   log.api("GET /users", { status: 200 });
 *   log.state("panier mis à jour", cart);
 *   log.error("échec paiement", err);
 *   log.perf("chargement page", 320); // ms
 *   log.info("message simple");
 */

export type LogCategory = "api" | "state" | "error" | "perf" | "info";

export interface LogEntry {
  id: number;
  timestamp: number;
  category: LogCategory;
  message: string;
  data?: unknown;
}

const isDev = process.env.NODE_ENV !== "production";

// Couleurs console par catégorie
const COLORS: Record<LogCategory, string> = {
  api: "#3b82f6", // bleu
  state: "#a855f7", // violet
  error: "#ef4444", // rouge
  perf: "#f59e0b", // orange
  info: "#6b7280", // gris
};

// Buffer en mémoire (lu par l'overlay). Limité pour ne pas grossir indéfiniment.
const MAX_BUFFER = 200;
let buffer: LogEntry[] = [];
let counter = 0;

// Abonnés (l'overlay s'abonne pour se re-rendre quand un log arrive)
type Listener = (entries: LogEntry[]) => void;
const listeners = new Set<Listener>();

function notify() {
  for (const l of listeners) l(buffer);
}

export function subscribeToLogs(listener: Listener): () => void {
  listeners.add(listener);
  listener(buffer); // état initial
  return () => listeners.delete(listener);
}

export function getLogs(): LogEntry[] {
  return buffer;
}

export function clearLogs(): void {
  buffer = [];
  notify();
}

function record(category: LogCategory, message: string, data?: unknown) {
  // En prod, on ne garde/affiche que les erreurs.
  if (!isDev && category !== "error") return;

  const entry: LogEntry = {
    id: ++counter,
    timestamp: Date.now(),
    category,
    message,
    data,
  };

  buffer = [...buffer, entry].slice(-MAX_BUFFER);
  notify();

  // Sortie console lisible (dev uniquement, sauf erreurs)
  if (isDev || category === "error") {
    const time = new Date(entry.timestamp).toLocaleTimeString();
    const tag = `%c[${category.toUpperCase()}] ${time}`;
    const css = `color:${COLORS[category]};font-weight:bold;`;
    if (category === "error") {
      console.error(tag, css, message, data ?? "");
    } else {
      console.log(tag, css, message, data ?? "");
    }
  }
}

export const log = {
  api: (message: string, data?: unknown) => record("api", message, data),
  state: (message: string, data?: unknown) => record("state", message, data),
  error: (message: string, data?: unknown) => record("error", message, data),
  perf: (message: string, ms?: number) =>
    record("perf", message, ms !== undefined ? { ms } : undefined),
  info: (message: string, data?: unknown) => record("info", message, data),
};

export { COLORS as LOG_COLORS };
